import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RefreshCcw, Lock, LockOpen, Copy, Download } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { fmtMoney, fmtNumber, errMsg } from '../../lib/format';

const Row = ({ label, value, bold, testid, accent }) => (
  <div className="flex items-center justify-between gap-3">
    <span className={`text-[13px] ${bold ? 'font-semibold' : 'text-muted-foreground'}`}>{label}</span>
    <span className={`money text-[13px] ${bold ? 'font-semibold' : ''} ${accent ? 'text-primary text-[15px]' : ''}`} data-testid={testid}>{value}</span>
  </div>
);

const PctInput = ({ value, onCommit, disabled, testid }) => {
  const [val, setVal] = useState(value);
  React.useEffect(() => setVal(value), [value]);
  return (
    <Input
      type="number"
      step="0.01"
      value={val ?? ''}
      disabled={disabled}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => { if (String(val) !== String(value)) onCommit(Number(val)); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
      className="h-7 w-20 text-right font-mono text-[12.5px]"
      data-testid={testid}
    />
  );
};

/**
 * Zona derecha: resumen con cascada de totales y configuración financiera.
 */
export const SummaryPanel = ({ quotation, frozen, editor }) => {
  const { canFinalize } = useAuth();
  const [fxLoading, setFxLoading] = useState(false);
  const t = quotation?.totals || {};
  const moneda = quotation?.moneda || 'MXN';
  const cascade = t.global_cascade;
  const scope = quotation?.quotation_type === 'pv' ? 'pv' : 'prefab';

  const { data: appSettings } = useQuery({
    queryKey: ['app-settings'],
    queryFn: async () => (await api.get('/settings')).data,
  });

  const patch = (p) => editor.updateQuotation.mutate(p);

  const toggleGlobal = (v) => {
    const p = { apply_global_indirects: v };
    // Al activar, sugerir defaults de Configuración si los % están en cero
    if (v) {
      const allZero = !quotation?.global_indirect_pct && !quotation?.global_financing_pct && !quotation?.global_utility_pct;
      const d = appSettings?.global_defaults?.[scope];
      if (allZero && d && (d.indirect_pct || d.financing_pct || d.utility_pct)) {
        p.global_indirect_pct = d.indirect_pct ?? 0;
        p.global_financing_pct = d.financing_pct ?? 0;
        p.global_utility_pct = d.utility_pct ?? 0;
      }
    }
    patch(p);
  };

  const refreshFx = async () => {
    setFxLoading(true);
    try {
      const { data } = await api.get('/exchange-rate');
      patch({ exchange_rate: data.rate });
      toast.success(`Tipo de cambio actualizado: ${fmtNumber(data.rate, 4)} (${data.source === 'public' ? 'API pública' : data.source})`);
    } catch (e) {
      toast.error(errMsg(e, 'No se pudo obtener el tipo de cambio'));
    } finally {
      setFxLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="apu-summary-panel">
      <div className="px-4 py-2.5 border-b border-border th-label">Resumen de cotización</div>
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {/* Configuración financiera */}
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">Moneda</Label>
                <Select value={moneda} onValueChange={(v) => patch({ moneda: v })} disabled={frozen}>
                  <SelectTrigger className="h-8" data-testid="summary-moneda-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="MXN">MXN</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">TC (MXN/USD)</Label>
                <div className="flex gap-1">
                  <PctInput value={quotation?.exchange_rate} disabled={frozen} testid="summary-tc-input" onCommit={(v) => patch({ exchange_rate: v })} />
                  <Button variant="outline" size="icon" className="h-7 w-7 shrink-0" onClick={refreshFx} disabled={frozen || fxLoading} data-testid="refresh-fx-button" title="Obtener tipo de cambio (Banxico/API pública)">
                    <RefreshCcw size={12} className={fxLoading ? 'animate-spin' : ''} />
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border p-2.5">
              <div>
                <div className="text-[12.5px] font-medium">Indirectos globales</div>
                <div className="text-[11px] text-muted-foreground">Cascada sobre el subtotal</div>
              </div>
              <Switch
                checked={!!quotation?.apply_global_indirects}
                disabled={frozen}
                onCheckedChange={toggleGlobal}
                data-testid="global-indirects-switch"
              />
            </div>

            {quotation?.apply_global_indirects && (
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1">
                  <Label className="text-[11px] text-muted-foreground">% Ind.</Label>
                  <PctInput value={quotation?.global_indirect_pct} disabled={frozen} testid="global-ind-input" onCommit={(v) => patch({ global_indirect_pct: v })} />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] text-muted-foreground">% Fin.</Label>
                  <PctInput value={quotation?.global_financing_pct} disabled={frozen} testid="global-fin-input" onCommit={(v) => patch({ global_financing_pct: v })} />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] text-muted-foreground">% Util.</Label>
                  <PctInput value={quotation?.global_utility_pct} disabled={frozen} testid="global-uti-input" onCommit={(v) => patch({ global_utility_pct: v })} />
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <Label className="text-[12.5px] text-muted-foreground">IVA %</Label>
              <PctInput value={quotation?.iva_percentage} disabled={frozen} testid="summary-iva-input" onCommit={(v) => patch({ iva_percentage: v })} />
            </div>
          </div>

          <Separator />

          {/* Cascada de totales */}
          <div className="space-y-2">
            <Row label="Subtotal conceptos" value={fmtMoney(t.subtotal, moneda)} testid="summary-subtotal" />
            {cascade && (
              <>
                <Row label={`+ Indirectos (${fmtNumber(quotation?.global_indirect_pct, 2)}%)`} value={fmtMoney(cascade.indirect_amount, moneda)} testid="summary-global-ind" />
                <Row label={`+ Financiamiento (${fmtNumber(quotation?.global_financing_pct, 2)}%)`} value={fmtMoney(cascade.financing_amount, moneda)} testid="summary-global-fin" />
                <Row label={`+ Utilidad (${fmtNumber(quotation?.global_utility_pct, 2)}%)`} value={fmtMoney(cascade.utility_amount, moneda)} testid="summary-global-uti" />
              </>
            )}
            <Row label="Subtotal antes de IVA" value={fmtMoney(t.subtotal_before_iva, moneda)} bold testid="summary-before-iva" />
            <Row label={`IVA (${fmtNumber(quotation?.iva_percentage, 2)}%)`} value={fmtMoney(t.iva_amount, moneda)} testid="summary-iva-amount" />
            <Separator />
            <Row label="TOTAL" value={fmtMoney(t.total, moneda)} bold accent testid="summary-total" />
          </div>

          <Separator />

          {/* Acciones */}
          <div className="space-y-2">
            <Button variant="outline" className="w-full justify-start" onClick={() => editor.recalculate.mutate()} disabled={frozen} data-testid="recalculate-button">
              <RefreshCcw size={14} className="mr-2" /> Recalcular totales
            </Button>
            {canFinalize && (frozen ? (
              <Button variant="outline" className="w-full justify-start" onClick={() => editor.reopen.mutate()} data-testid="reopen-button">
                <LockOpen size={14} className="mr-2" /> Reabrir cotización
              </Button>
            ) : (
              <Button className="w-full justify-start" onClick={() => editor.finalize.mutate()} data-testid="finalize-button">
                <Lock size={14} className="mr-2" /> Finalizar (congelar precios)
              </Button>
            ))}
          </div>

          {quotation?.fx_snapshot && (
            <p className="text-[11px] text-muted-foreground">
              FX snapshot: {fmtNumber(quotation.fx_snapshot.rate, 4)} · {quotation.fx_snapshot.source}
            </p>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
